// The ONE renderer of a move refusal's sentence (#6771, epic #6552; prototype moveWhy
// `:1598`, targetWhy `:1621`, "That move can no longer be undone" `:3146`). Exhaustive over
// the domain's `MOVE_REFUSAL_REASONS` (`packages/domain/src/entities/runner/move-verdict.ts`):
// a reason the domain adds is a type error in `moveRefusalText`'s switch, never a silent
// `undefined`. The drop panels, Move to's options, the drag's toast and announcements, and
// #6772 / #6773 (Undo, the status card) all render a refusal through it.
//
// EVERY NUMBER IS THE REFUSAL'S OWN. C is `needs`, the moves left is `left`, and "N s" counts
// down to `free_at` (`secondsUntil`, `./time.ts`: a null `free_at` is 1 s, as the server's
// `moveRetryAfterSeconds` answers). The one number from elsewhere is `no_room`'s CPU, the
// preview's own `cpu.after` for that target: the server never refuses on a projection, so
// the prototype's `> 100%` tests are not ported, and `no_room` keys on the wire's `room`.
//
// `capability_off` names ONE job (the refusal's `item`), so only the singular form exists;
// an `item` that is not a job on the card reads "This kind of job" (a listed deviation).

import type { MoveRefusalReason } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import type { MoveRefusalWire } from './move-calls';
import { secondsUntil } from './time';

/** What a sentence reads beyond the refusal. */
export type MoveCopyContext = {
  /** A machine's name by id; null when this read does not hold it (the 8-char id shows). */
  readonly machineName: (id: string | null) => string | null;
  /** The capability name of the job a refusal's `item` names ("Coder"); null when unknown. */
  readonly roleOfItem: (item: string | null) => string | null;
  /** The preview's projected CPU on the refusing target, busy percent; null when unknown. */
  readonly targetCpuAfter: number | null;
  /** The clock "N s" counts down from (a ticking signal's reading). */
  readonly nowMs: number;
};

/** The base key of each reason. A literal record, so every key is visible to the i18n gates. */
export const MOVE_REFUSAL_KEY: Readonly<Record<MoveRefusalReason, string>> = {
  item_gone: 'fleet.move.why_item_gone',
  epic_empty: 'fleet.move.why_epic_empty',
  group_empty: 'fleet.move.why_group_empty',
  undo_unavailable: 'fleet.move.why_undo_unavailable',
  source_offline: 'fleet.move.why_source_offline',
  waiting_on_answer: 'fleet.move.why_waiting_on_answer',
  still_starting: 'fleet.move.why_still_starting',
  still_moving: 'fleet.move.why_still_moving',
  lane_not_pausable: 'fleet.move.why_lane_not_pausable',
  stop_requested: 'fleet.move.why_stop_requested',
  cannot_checkpoint: 'fleet.move.why_cannot_checkpoint',
  cooling_down: 'fleet.move.why_cooling_down',
  source_budget: 'fleet.move.why_source_budget',
  transfers_in_flight: 'fleet.move.why_transfers_in_flight',
  already_there: 'fleet.move.why_already_there',
  target_offline: 'fleet.move.why_target_offline',
  capability_off: 'fleet.move.why_capability_off',
  no_room: 'fleet.move.why_no_room',
  other_company_there: 'fleet.move.why_other_company_there',
  repo_already_there: 'fleet.move.why_repo_already_there',
  target_budget: 'fleet.move.why_target_budget',
};

/** The machine a refusal names, by name, else its 8-char id, else nothing to name. */
function machineOf(r: MoveRefusalWire, ctx: MoveCopyContext): string {
  const id = r.machine_id ?? null;
  return ctx.machineName(id) ?? (id === null ? '' : id.slice(0, 8));
}

/** A budget refusal: "has N move(s) left …", or at 0 "has used / taken its moves …". */
function budgetText(
  r: MoveRefusalWire,
  keys: { readonly some: string; readonly none: string },
  vars: Readonly<Record<string, string | number>>,
  tr: Translator,
): string {
  const left = r.left ?? 0;
  if (left <= 0) return tr(keys.none, vars);
  return tr.plural(keys.some, left, { ...vars, count: left, needs: r.needs ?? 1 });
}

/** `no_room`, keyed on the room's own refusal word. */
function noRoomText(
  r: MoveRefusalWire,
  name: string,
  ctx: MoveCopyContext,
  tr: Translator,
): string {
  const refusal = r.room?.refusal ?? null;
  if (refusal === 'memory_low') return tr('fleet.move.why_no_room_memory', { name });
  if (refusal === 'cpu_busy' && ctx.targetCpuAfter !== null)
    return tr('fleet.move.why_no_room_cpu', { name, percent: Math.round(ctx.targetCpuAfter) });
  return tr('fleet.move.why_no_room', { name });
}

/** The sentence for a refusal, every number from its own fields. */
export function moveRefusalText(r: MoveRefusalWire, ctx: MoveCopyContext, tr: Translator): string {
  const name = machineOf(r, ctx);
  const freeAt = r.free_at == null ? null : Date.parse(r.free_at);
  const seconds = secondsUntil(freeAt, ctx.nowMs);
  const key = MOVE_REFUSAL_KEY[r.reason];
  const reason: MoveRefusalReason = r.reason;
  switch (reason) {
    case 'item_gone':
    case 'epic_empty':
    case 'group_empty':
    case 'undo_unavailable':
    case 'still_moving':
    case 'waiting_on_answer':
    case 'still_starting':
    case 'lane_not_pausable':
    case 'stop_requested':
    case 'transfers_in_flight':
      return tr(key);
    case 'source_offline':
    case 'already_there':
    case 'target_offline':
    case 'other_company_there':
    case 'repo_already_there':
    case 'cannot_checkpoint':
      return tr(key, { name });
    case 'cooling_down':
      return tr(key, { seconds });
    case 'source_budget':
      return budgetText(
        r,
        { some: key, none: 'fleet.move.why_source_budget_none' },
        { name, seconds },
        tr,
      );
    case 'target_budget':
      return budgetText(
        r,
        { some: key, none: 'fleet.move.why_target_budget_none' },
        { name, seconds },
        tr,
      );
    case 'capability_off': {
      const role = ctx.roleOfItem(r.item ?? null);
      return role === null
        ? tr('fleet.move.why_capability_off_unknown', { name })
        : tr(key, { role, name });
    }
    case 'no_room':
      return noRoomText(r, name, ctx, tr);
    default: {
      const unhandled: never = reason;
      return unhandled;
    }
  }
}

/**
 * The refusal while it still stands at `nowMs`: a timed one (a `free_at`) stands only until
 * then, after which the move is the server's to judge again; one with no time stands. Every
 * renderer of a preview's or a move's refusal reads it through this with the page's ticking
 * clock, so a passed wait never leaves a stale countdown or a disabled option behind.
 */
export function standingRefusal<R extends Pick<MoveRefusalWire, 'free_at'>>(
  r: R | null | undefined,
  nowMs: number,
): R | null {
  if (r == null) return null;
  if (r.free_at == null) return r;
  const at = Date.parse(r.free_at);
  return Number.isNaN(at) || at > nowMs ? r : null;
}
